import React, { useEffect, useState } from 'react';
import { Rnd } from 'react-rnd';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import {
    Button,
    Grid,
    Typography,
    Card,
    CardMedia,
    CardContent,
} from '@material-ui/core';

const PREVIEW_WIDTH = 600;
const PREVIEW_HEIGHT = 400;

export default function LogoOverlayEditor() {
    const [photos, setPhotos] = useState([]);
    const [logos, setLogos] = useState([]); // { url, x, y, width, height, aspect }
    const [results, setResults] = useState([]);

    const handleLogoUpload = async (e) => {
        const files = Array.from(e.target.files);

        const loaded = await Promise.all(
            files.map((file) => {
                const url = URL.createObjectURL(file);
                return new Promise((resolve) => {
                    const img = new Image();
                    img.onload = () => {
                        const aspect = img.naturalWidth / img.naturalHeight;
                        resolve({
                            url,
                            x: 0,
                            y: 0,
                            width: 100,
                            height: 100 / aspect,
                            aspect,
                        });
                    };
                    img.src = url;
                });
            })
        );

        setLogos((prev) => [...prev, ...loaded]);
    };

    const resetPositions = () => {
        const reset = logos.map((logo) => ({
            ...logo,
            x: 0,
            y: 0,
            width: 100,
            height: 100 / logo.aspect,
        }));
        setLogos(reset);
    };

    const handlePhotoUpload = (e) => {
        const files = Array.from(e.target.files);
        setPhotos(files);
    };

    const loadImage = (src) =>
        new Promise((resolve) => {
            const img = new Image();
            img.onload = () => resolve(img);
            img.crossOrigin = 'anonymous';
            img.src = src;
        });

    const handleExport = async () => {
        const newResults = [];

        for (const file of photos) {
            const img = await loadImage(URL.createObjectURL(file));
            const canvas = document.createElement('canvas');
            const ctx = canvas.getContext('2d');
            canvas.width = img.width;
            canvas.height = img.height;
            ctx.drawImage(img, 0, 0);

            const scaleX = img.width / PREVIEW_WIDTH;
            const scaleY = img.height / PREVIEW_HEIGHT;

            for (const logo of logos) {
                const logoImg = await loadImage(logo.url);
                ctx.drawImage(
                    logoImg,
                    logo.x * scaleX,
                    logo.y * scaleY,
                    logo.width * scaleX,
                    logo.height * scaleY
                );
            }

            newResults.push(canvas.toDataURL('image/jpeg'));
        }

        setResults(newResults);
    };

    const downloadAll = async () => {
        const zip = new JSZip();
        results.forEach((dataUrl, index) => {
            const base64 = dataUrl.replace(/^data:image\/(png|jpeg);base64,/, '');
            zip.file(`imagen_${index + 1}.jpg`, base64, { base64: true });
        });

        const content = await zip.generateAsync({ type: 'blob' });
        saveAs(content, 'imagenes_con_logos.zip');
    };

    return (
        <div style={{ padding: 20 }}>

            <Typography style={{ margin: 20 }} variant="h6">Sube los logos del evento o patrocinadores, ubícalos visualmente sobre un área de referencia y agrégalos en lote a tus fotos. Todo el proceso se realiza de forma local (sin subir archivos al servidor) y al finalizar podrás descargar las imágenes con logos en un archivo comprimido listo para compartir</Typography>



            <Typography variant="h6">1. Carga los logos y ajustalos a la posición y tamaño que deseas en las imágenes</Typography>
            <input
                type="file"
                accept="image/*"
                multiple
                onChange={handleLogoUpload}
                style={{ marginBottom: 10 }}
            />

            <div
                style={{
                    position: 'relative',
                    width: PREVIEW_WIDTH,
                    height: PREVIEW_HEIGHT,
                    backgroundColor: '#e0e0e0',
                    border: '1px solid #ccc',
                    marginBottom: 20,
                    borderColro: "#000000"
                }}
            >
                {logos.map((logo, index) => (
                    <Rnd
                        key={index}
                        default={{
                            x: logo.x,
                            y: logo.y,
                            width: logo.width,
                            height: logo.height,
                        }}
                        lockAspectRatio={logo.aspect}
                        onDragStop={(e, d) => {
                            const updated = [...logos];
                            updated[index].x = d.x;
                            updated[index].y = d.y;
                            setLogos(updated);
                        }}
                        onResizeStop={(e, dir, ref, delta, position) => {
                            const updated = [...logos];
                            updated[index].width = parseFloat(ref.style.width);
                            updated[index].height = parseFloat(ref.style.height);
                            updated[index].x = position.x;
                            updated[index].y = position.y;
                            setLogos(updated);
                        }}
                        bounds="parent"
                    >
                        <img
                            src={logo.url}
                            alt={`logo-${index}`}
                            style={{ width: '100%', height: '100%' }}
                        />
                    </Rnd>
                ))}
            </div>

            {/* {logos.length > 0 && (
                <Button
                    variant="outlined"
                    onClick={resetPositions}
                    style={{ marginBottom: 20 }}
                >
                    Resetear posiciones de logos
                </Button>
            )} */}

            <Typography variant="h6">2. Subir imágenes</Typography>
            <input type="file" accept="image/*" multiple onChange={handlePhotoUpload} />

            <div style={{ marginTop: 20 }}>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleExport}
                    disabled={photos.length === 0 || logos.length === 0}
                >
                    Aplicar logos a todas las fotos
                </Button>

                {results.length > 0 && (
                    <Button
                        variant="contained"
                        color="secondary"
                        onClick={downloadAll}
                        style={{ marginLeft: 20 }}
                    >
                        Descargar todas en ZIP
                    </Button>
                )}
            </div>

            {results.length > 0 && (
                <div style={{ marginTop: 40 }}>
                    <Typography variant="h6">3. Imágenes generadas</Typography>
                    <Grid container spacing={2}>
                        {results.map((src, index) => (
                            <Grid item xs={12} sm={6} md={4} key={index}>
                                <Card>
                                    <CardMedia
                                        component="img"
                                        image={src}
                                        title={`Imagen ${index + 1}`}
                                    />
                                    <CardContent>
                                        <Typography variant="body2">{`Imagen ${index + 1}`}</Typography>
                                    </CardContent>
                                </Card>
                            </Grid>
                        ))}
                    </Grid>
                </div>
            )}
        </div>
    );
}
